import type { WeatherState } from './types';

/**
 * Sursă simulată pentru modul demo (`config.json` → `demo: true`), ca
 * frontend-ul să fie utilizabil înainte să existe backend-ul. Valorile pleacă
 * de la un ciclu diurn plauzibil și primesc o mică deviație la fiecare tick.
 */

const TICK_MS = 4000;

function wobble(value: number, amount: number, min: number, max: number): number {
  const next = value + (Math.random() - 0.5) * amount;
  return Math.max(min, Math.min(max, next));
}

function round(value: number, decimals = 1): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function conditionFor(uv: number, rainRate: number): string {
  if (rainRate > 2) return 'pouring';
  if (rainRate > 0) return 'rainy';
  if (uv > 5) return 'sunny';
  if (uv > 2) return 'partlycloudy';
  return 'cloudy';
}

export function createMockStream(onState: (state: WeatherState) => void): () => void {
  // Amplitudinea diurnă: caldă și însorită la prânz, rece și întunecată noaptea.
  const hour = new Date().getHours() + new Date().getMinutes() / 60;
  const daylight = Math.max(0, Math.sin(((hour - 6) / 12) * Math.PI));

  let temperature = 12 + daylight * 16;
  let humidity = 75 - daylight * 30;
  let windSpeed = 8;
  let windDirection = 248;
  let pressure = 1013.6;
  let uv = daylight * 8;
  let illuminance = daylight * 92_000;
  let rainRate = 0;
  let precipitation = 0.4;
  let battery = 100;
  let capacitor = 2.72;

  const emit = () => {
    temperature = wobble(temperature, 0.4, -20, 45);
    humidity = wobble(humidity, 2, 10, 100);
    windSpeed = wobble(windSpeed, 3, 0, 55);
    windDirection = (windDirection + (Math.random() - 0.5) * 18 + 360) % 360;
    pressure = wobble(pressure, 0.3, 975, 1045);
    uv = wobble(uv, 0.5, 0, 12);
    illuminance = wobble(illuminance, 4000, 0, 120_000);
    rainRate = Math.random() < 0.9 ? 0 : wobble(rainRate + 1, 2, 0, 12);
    precipitation = round(precipitation + rainRate / 900, 2);
    battery = wobble(battery, 0.2, 0, 100);
    capacitor = wobble(capacitor, 0.02, 2.2, 3.3);

    const gust = windSpeed + Math.random() * 12;
    // Aproximarea Magnus pentru punctul de rouă.
    const gamma =
      Math.log(humidity / 100) + (17.62 * temperature) / (243.12 + temperature);
    const dewPoint = (243.12 * gamma) / (17.62 - gamma);

    onState({
      temperature: round(temperature),
      apparent_temperature: round(temperature + (humidity > 60 ? 2.1 : -0.6)),
      dew_point: round(dewPoint),
      humidex: round(temperature + 3.8),
      wind_chill: round(temperature - windSpeed * 0.05),
      heat_stress: temperature > 32 ? 'hot' : temperature > 27 ? 'warm' : 'comfortable',
      humidity: Math.round(humidity),

      wind_speed: round(windSpeed),
      gust_speed: round(gust),
      wind_direction: Math.round(windDirection),

      precipitation,
      rain_rate: round(rainRate),
      rain_status: rainRate > 0 ? 'raining' : 'none',

      pressure: round(pressure),
      pressure_trend: null,

      uv_index: round(uv),
      illuminance: Math.round(illuminance),

      battery: Math.round(battery),
      linkquality: Math.round(wobble(190, 30, 0, 255)),
      capacitor_voltage: round(capacitor, 2),
      voltage: round(wobble(4.94, 0.05, 3.6, 5.2), 2),

      weather_condition: conditionFor(uv, rainRate),
      last_seen: new Date().toISOString(),
    });
  };

  emit();
  const id = window.setInterval(emit, TICK_MS);
  return () => window.clearInterval(id);
}
